/**
 * VerifyPhoneBanner
 * -----------------
 * Sticky banner for signed-in users whose phone is not yet verified.
 * - Shows the SMS verification wizard inline (2-step: send code → enter code).
 * - Shows a countdown to the 24h grace deadline for email-signup users.
 * - Auto-hides once phone_verified becomes true.
 *
 * Drop this into any layout - it fetches its own state from /api/auth/me.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Phone, X, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  return `${h}h ${m}m`;
}

export default function VerifyPhoneBanner() {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(sessionStorage.getItem('verify-phone-dismissed') === '1');
  const [expanded, setExpanded] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/auth/verify-phone/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setState(r.data))
      .catch(() => {});
  }, []);

  if (!state || state.phone_verified || dismissed) return null;

  const graceLeft = timeUntil(state.must_verify_phone_by);
  const isUrgent = state.grace_expired;

  const sendCode = async () => {
    setError(''); setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/verify-phone/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not send code');
    }
    setLoading(false);
  };

  const submitCode = async () => {
    setError(''); setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/verify-phone/complete`, { code }, { headers: { Authorization: `Bearer ${token}` } });
      setState({ ...state, phone_verified: true });
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Incorrect code');
    }
    setLoading(false);
  };

  return (
    <div
      className={`w-full ${isUrgent ? 'bg-red-50 border-b border-red-200' : 'bg-amber-50 border-b border-amber-200'}`}
      data-testid="verify-phone-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-start gap-3">
          <Phone className={`w-5 h-5 mt-0.5 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-900">
                {isUrgent
                  ? 'Verify your phone to keep using Zeilian'
                  : 'Please verify your phone number'}
              </p>
              <button
                onClick={() => { setDismissed(true); sessionStorage.setItem('verify-phone-dismissed', '1'); }}
                className="text-gray-400 hover:text-gray-600"
                data-testid="verify-phone-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-700 mt-1">
              {isUrgent
                ? 'Your 24-hour grace period has ended. You must verify your phone number to post ads or contact sellers.'
                : `You have ${graceLeft || 'a limited time'} left to verify your phone by SMS. Ads cannot be posted from unverified accounts.`}
            </p>
            {!expanded && (
              <Button size="sm" className="mt-2" onClick={() => setExpanded(true)} data-testid="verify-phone-expand-btn">
                Verify now
              </Button>
            )}
            {expanded && !sent && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-gray-700">We'll send a 6-digit code to <b>{state.phone}</b>.</p>
                <Button size="sm" onClick={sendCode} disabled={loading} data-testid="verify-phone-send-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send code by SMS
                </Button>
              </div>
            )}
            {expanded && sent && (
              <div className="mt-3 space-y-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="max-w-xs"
                  data-testid="verify-phone-code-input"
                />
                {error && (
                  <Alert variant="destructive" className="max-w-md"><AlertCircle className="w-4 h-4" /><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={submitCode} disabled={loading || code.length < 4} data-testid="verify-phone-confirm-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    Verify
                  </Button>
                  <Button size="sm" variant="ghost" onClick={sendCode} disabled={loading}>Resend</Button>
                </div>
              </div>
            )}
            {error && !expanded && (
              <Alert variant="destructive" className="max-w-md mt-2"><AlertCircle className="w-4 h-4" /><AlertDescription>{error}</AlertDescription></Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
